export interface QtyParseResult {
  ok: boolean;
  value: number;
  message?: string;
}

export interface TakeCheckResult {
  ok: boolean;
  message?: string;
}

/** Integer quantity of at least 1 (issues, transfers, receipts, reservations). */
export function parsePositiveInt(value: unknown, label = 'Quantity'): QtyParseResult {
  if (value === '' || value === null || value === undefined) {
    return { ok: false, value: 0, message: `${label} is required.` };
  }
  if (typeof value === 'string' && value.trim() === '') {
    return { ok: false, value: 0, message: `${label} is required.` };
  }
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) {
    return { ok: false, value: 0, message: `${label} must be a number.` };
  }
  if (!Number.isInteger(num)) {
    return { ok: false, value: 0, message: `${label} must be a whole number.` };
  }
  if (num < 1) {
    return { ok: false, value: 0, message: `${label} must be at least 1.` };
  }
  return { ok: true, value: num };
}

/** Finite number ≥ 0 (prices, reorder, set-to quantity). */
export function parseNonNegativeNumber(value: unknown, label = 'Value'): QtyParseResult {
  if (value === '' || value === null || value === undefined) {
    return { ok: false, value: 0, message: `${label} is required.` };
  }
  if (typeof value === 'string' && value.trim() === '') {
    return { ok: false, value: 0, message: `${label} is required.` };
  }
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) {
    return { ok: false, value: 0, message: `${label} must be a number.` };
  }
  if (num < 0) {
    return { ok: false, value: 0, message: `${label} cannot be negative.` };
  }
  return { ok: true, value: num };
}

export function remainingAvailable(onHand: number, reserved = 0): number {
  const hand = Number.isFinite(onHand) ? onHand : 0;
  const hold = Number.isFinite(reserved) ? reserved : 0;
  return Math.max(0, hand - hold);
}

export function assertCanTake(
  available: number,
  requested: number,
  label: string
): TakeCheckResult {
  const avail = remainingAvailable(available, 0);
  if (!Number.isFinite(requested) || requested < 1) {
    return { ok: false, message: `Enter a valid quantity for ${label}.` };
  }
  if (requested > avail) {
    return {
      ok: false,
      message: `Only ${avail} available for ${label}. Cannot take ${requested}.`
    };
  }
  return { ok: true };
}

export function requiredText(value: unknown, label: string): QtyParseResult {
  const text = String(value ?? '').trim();
  if (!text) {
    return { ok: false, value: 0, message: `${label} is required.` };
  }
  return { ok: true, value: 0 };
}
